import { lazy, type ComponentType } from "react";

/**
 * React.lazy with transient-failure recovery.
 *
 * Dynamic imports fail for two common, non-architectural reasons:
 *  1. a flaky/slow network fetch of the chunk (crawlers and mobile hit this most), and
 *  2. a stale chunk hash after a redeploy while the tab is still open.
 *
 * Both are recoverable. We retry the import a couple of times with a small
 * backoff and a cache-busting query, and only then let the error propagate to
 * the ErrorBoundary. This keeps lazy loading (and its performance benefit)
 * fully intact while preventing a module-load blip from becoming the page's
 * primary content for a user or for Googlebot.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries = 2,
  baseDelayMs = 350,
) {
  return lazy(() => loadWithRetry(factory, retries, baseDelayMs));
}

export async function loadWithRetry<T>(
  factory: () => Promise<{ default: T }>,
  retries: number,
  baseDelayMs: number,
): Promise<{ default: T }> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await factory();
    } catch (error) {
      lastError = error;
      if (attempt === retries) break;
      await new Promise((r) => setTimeout(r, baseDelayMs * (attempt + 1)));
    }
  }
  throw lastError;
}
